import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { t } from 'services/translation';

import { SearchFilter } from 'ui_system/inputs/search/SearchFilter';

import {
  fetchExperiences,
  clearSearch,
} from 'data_system/affiliate_portal/actions/experienceActions';
import { getExperienceSlice } from 'data_system/affiliate_portal/selectors';
import { getBrand } from 'data_system/token_auth/selectors';

import { ExperienceCard } from './ExperienceCard';
import { TruncatedGradient } from './styles';

const midCssBreak = '800px';
const smallCssBreak = '600px';

const Experiences = styled.div`
  width: 768px;
  margin: 30px auto;
  position: relative;
  ${(props) => props.isLoading && 'height: calc(100vh - 200px);'}
  ${(props) => props.isLoading && 'overflow: hidden;'}

  opacity: ${(props) => (props.fade ? '0.4' : '1')};

  @media (max-width: ${midCssBreak}) {
    width: calc(100% - 30px);
  }
  @media (max-width: ${smallCssBreak}) {
    width: 250px;
  }
`;

const NoResults = styled.div`
  padding: 10px;
  margin: 60px 0 10px;
  color: #999;
  font-size: 16px;
  text-align: center;
  overflow-wrap: break-word;

  span {
    font-weight: bold;
  }

  @media (max-width: ${smallCssBreak}) {
    font-size: 14px;
  }
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: ${smallCssBreak}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: bold;

  @media (max-width: ${smallCssBreak}) {
    margin-bottom: 15px;
  }
`;

export function ExperienceList() {
  const dispatch = useDispatch();
  const experienceSlice = useSelector(getExperienceSlice);
  const { name } = useSelector(getBrand);
  const { experiences, firstLoad, fetching, error, search } = experienceSlice;
  const [currentSearch, setSearch] = useState(search);

  const isLoading = firstLoad || fetching;

  useEffect(() => {
    if (firstLoad) {
      document.title = `${name} - ${t('AFFILIATE_PORTAL')}`;
      dispatch(fetchExperiences());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderExperienceCards() {
    // Loading state
    if (isLoading) {
      // eslint-disable-next-line react/no-array-index-key
      return [...Array(8)].map((_, key) => (
        <ExperienceCard key={key} isLoading />
      ));
    }

    // Error or no search results
    if (error || (!fetching && experiences.length === 0)) {
      let noResultsText = t('NO_RESULTS');
      if (search) {
        noResultsText = (
          <Fragment>
            {t('NO_RESULTS_FOR')} <span>:{search}</span>.
          </Fragment>
        );
      }

      return <NoResults>{noResultsText}</NoResults>;
    }

    // Render experience list
    return experiences.map((experience) => (
      <ExperienceCard key={experience.id} experience={experience.attributes} />
    ));
  }

  return (
    <Experiences isLoading={isLoading}>
      <Top>
        <Title data-testid="affiliate-portal-title-experience">
          {t('EXPERIENCES')}
        </Title>
        <SearchFilter
          query={currentSearch}
          updateQuery={(_search) => setSearch(_search)}
          applyFilter={() => dispatch(fetchExperiences(currentSearch))}
          clearFilter={() => {
            setSearch('');
            dispatch(clearSearch);
          }}
        />
      </Top>
      {renderExperienceCards()}
      {isLoading && <TruncatedGradient height={250} bottom={0} />}
    </Experiences>
  );
}
